import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  extractCrmError,
  userCanEditCrm,
  userHasCrmAccess,
  CLIENT_STATUSES,
  DEAL_STAGES,
  TAG_COLORS,
  TAG_COLOR_HEX,
  getCustomerOrderSourceLabel,
  getCustomerOrderStatusLabel,
  getCustomerPaymentMethodLabel,
  getCustomerPaymentStatusLabel,
} from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatCrmDate, formatCrmDateTime } from "@/utils/crmDateTime";
import {
  ArrowLeft,
  Edit,
  Plus,
  Trash2,
  Star,
  Loader2,
  MessageSquare,
  Car,
  TagIcon,
  Phone,
  ClipboardList,
  CalendarClock,
  CheckCircle2,
  Send,
  Package,
  Wallet,
  History,
  BellRing,
} from "lucide-react";

const TABS = [
  { value: "overview", label: "Обзор" },
  { value: "messages", label: "Сообщения" },
  { value: "requests", label: "Запросы" },
  { value: "stock", label: "Ждут наличия" },
  { value: "deals", label: "Сделки" },
  { value: "tags", label: "Теги" },
  { value: "vehicles", label: "Автомобили" },
  { value: "orders", label: "Заказы" },
  { value: "payments", label: "Оплаты" },
];

const STOCK_STATUS_LABELS = {
  active: "Ждёт уведомления",
  notified: "Уведомлён",
  cancelled: "Отменено",
};

const STOCK_STATUS_STYLES = {
  active: "border-amber-200 bg-amber-50 text-amber-800",
  notified: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

function fmt(dt) {
  return formatCrmDateTime(dt);
}

function fmtDate(dt) {
  return formatCrmDate(dt);
}

const STATUS_STYLES = {
  new: "border-blue-200 bg-blue-50 text-blue-700",
  reviewed: "border-slate-200 bg-slate-50 text-slate-700",
  in_progress: "border-amber-200 bg-amber-50 text-amber-700",
  exported: "border-emerald-200 bg-emerald-50 text-emerald-700",
  deal: "border-green-200 bg-green-50 text-green-700",
  ignored: "border-gray-200 bg-gray-50 text-gray-500",
  blocked: "border-red-200 bg-red-50 text-red-700",
};

const CUSTOMER_ORDER_STATUS_STYLES = {
  draft: "border-slate-200 bg-slate-50 text-slate-700",
  awaiting_confirmation: "border-amber-200 bg-amber-50 text-amber-700",
  confirmed: "border-blue-200 bg-blue-50 text-blue-700",
  in_work: "border-orange-200 bg-orange-50 text-orange-700",
  partially_arrived: "border-amber-300 bg-amber-100 text-amber-900 font-semibold",
  ready: "border-cyan-200 bg-cyan-50 text-cyan-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-red-200 bg-red-50 text-red-700",
  needs_revision: "border-violet-200 bg-violet-50 text-violet-700",
};

function formatOrderMoney(value, currency = "KZT") {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value))} ${currency}`.trim();
}

const VEHICLE_SOURCE_LABELS = {
  manual: "Добавлено вручную",
  parser: "Распознано из сообщения",
  import: "Импортировано",
  legacy: "Перенесено из старой CRM",
  whatsapp: "Из WhatsApp",
};

const MESSAGE_TYPE_LABELS = {
  textMessage: "Текст",
  extendedTextMessage: "Текст",
  imageMessage: "Изображение",
  videoMessage: "Видео",
  documentMessage: "Документ",
  audioMessage: "Аудио",
  voiceMessage: "Голосовое",
  locationMessage: "Геолокация",
  contactMessage: "Контакт",
  stickerMessage: "Стикер",
};

const MESSAGE_PAGE_SIZE = 10;

const IMAGE_MESSAGE_TYPES = new Set(["imageMessage", "stickerMessage"]);
const AUDIO_MESSAGE_TYPES = new Set(["audioMessage", "voiceMessage"]);

function getStatusLabel(status) {
  return CLIENT_STATUSES.find((s) => s.value === status)?.label || "Без статуса";
}

function getDealStageLabel(stage) {
  return DEAL_STAGES.find((s) => s.value === stage)?.label || "Стадия не указана";
}

function formatConfidence(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "Не указана";
  return `${Math.round(Number(value) * 100)}%`;
}

function ClientStatusBadge({ status }) {
  return (
    <Badge
      variant="outline"
      className={`rounded-full px-2.5 py-1 font-medium ${STATUS_STYLES[status] || "border-slate-200 bg-slate-50 text-slate-700"}`}
    >
      {getStatusLabel(status)}
    </Badge>
  );
}

function DetailMetric({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

const ACTIVITY_SEVERITY_STYLES = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  error: "border-red-200 bg-red-50 text-red-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  info: "border-slate-200 bg-slate-50 text-slate-700",
};

function ActivityFeed({ token, clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await crmApi.getClientActivity(token, clientId, { limit: 100 });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить историю событий"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаем историю событий...
      </div>
    );
  }

  if (items.length === 0) {
    return <EmptyState>Событий по этому клиенту пока нет.</EmptyState>;
  }

  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.id} className="rounded-lg border border-slate-200 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-slate-950">{item.title}</p>
                <Badge
                  variant="outline"
                  className={ACTIVITY_SEVERITY_STYLES[item.severity] || ACTIVITY_SEVERITY_STYLES.info}
                >
                  {item.kind}
                </Badge>
              </div>
              {item.summary && (
                <p className="mt-1 text-sm text-slate-600">{item.summary}</p>
              )}
              {item.details?.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-slate-500">
                  {item.details.map((detail, idx) => (
                    <li key={`${item.id}-detail-${idx}`}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
            <p className="shrink-0 text-xs text-slate-400">{fmt(item.timestamp)}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// -----------------------------------------------------------------------------
// Edit client dialog
// -----------------------------------------------------------------------------

function ClientEditDialog({ open, onOpenChange, client, onSaved, token }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client && open) {
      setForm({
        manual_name: client.manual_name || "",
        customer_name: client.customer_name || "",
        status: client.status,
        notes: client.notes || "",
        auto_reply_disabled: client.auto_reply_disabled,
      });
    }
  }, [client, open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const updated = await crmApi.patchClient(token, client.id, form);
      toast.success("Изменения сохранены");
      onSaved(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Редактирование клиента</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Имя в карточке</Label>
            <Input
              value={form.manual_name || ""}
              placeholder="Как менеджеры будут видеть клиента"
              onChange={(e) => setForm({ ...form, manual_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Имя из переписки</Label>
            <Input
              value={form.customer_name || ""}
              placeholder="Имя, которое пришло из источника"
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Статус</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Заметки менеджера</Label>
            <Textarea
              rows={4}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!form.auto_reply_disabled}
              onCheckedChange={(c) => setForm({ ...form, auto_reply_disabled: !!c })}
              id="auto-reply-disabled"
            />
            <Label htmlFor="auto-reply-disabled" className="cursor-pointer">
              Отключить автоответы
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Tag form
// -----------------------------------------------------------------------------

function TagDialog({ open, onOpenChange, clientId, initial, onSaved, token }) {
  const [form, setForm] = useState({ tag: "", color: "secondary" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ tag: initial?.tag || "", color: initial?.color || "secondary" });
    }
  }, [open, initial]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (initial?.id) {
        await crmApi.patchTag(token, initial.id, form);
      } else {
        await crmApi.createTag(token, clientId, form);
      }
      toast.success("Тег сохранён");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Изменить тег" : "Добавить тег"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Тег</Label>
            <Input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} required />
          </div>
          <div>
            <Label>Цвет</Label>
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TAG_COLORS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Vehicle form
// -----------------------------------------------------------------------------

function VehicleDialog({ open, onOpenChange, clientId, initial, onSaved, token }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(initial || { source: "manual", is_primary: false });
    }
  }, [open, initial]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.year === "") payload.year = null;
      if (initial?.id) {
        await crmApi.patchVehicle(token, initial.id, payload);
      } else {
        await crmApi.createVehicle(token, clientId, payload);
      }
      toast.success("Автомобиль сохранён");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Изменить авто" : "Добавить авто"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Бренд</Label>
              <Input value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div>
              <Label>Модель</Label>
              <Input value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div>
              <Label>Год</Label>
              <Input
                type="number"
                value={form.year ?? ""}
                onChange={(e) => setForm({ ...form, year: e.target.value ? parseInt(e.target.value, 10) : null })}
              />
            </div>
            <div>
              <Label>Кузов</Label>
              <Input value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
            <div>
              <Label>Двигатель</Label>
              <Input value={form.engine || ""} onChange={(e) => setForm({ ...form, engine: e.target.value })} />
            </div>
            <div>
              <Label>VIN</Label>
              <Input value={form.vin || ""} onChange={(e) => setForm({ ...form, vin: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Комментарий</Label>
            <Textarea
              rows={2}
              value={form.comment || ""}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!form.is_primary}
              onCheckedChange={(c) => setForm({ ...form, is_primary: !!c })}
              id="is-primary"
            />
            <Label htmlFor="is-primary" className="cursor-pointer">Основной автомобиль</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Deal / Request forms
// -----------------------------------------------------------------------------

function DealDialog({ open, onOpenChange, clientId, initial, onSaved, token }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(initial || { stage: "inquiry" });
  }, [open, initial]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (initial?.id) {
        await crmApi.patchDeal(token, initial.id, form);
      } else {
        await crmApi.createDeal(token, clientId, form);
      }
      toast.success("Сделка сохранена");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Изменить сделку" : "Новая сделка"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Название *</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Авто</Label>
              <Input value={form.car_model || ""} onChange={(e) => setForm({ ...form, car_model: e.target.value })} />
            </div>
            <div>
              <Label>Сумма</Label>
              <Input value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Стадия</Label>
            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEAL_STAGES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Заметки</Label>
            <Textarea
              rows={2}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ open, onOpenChange, clientId, initial, onSaved, token }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(initial || { confidence: 0 });
  }, [open, initial]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (initial?.id) {
        await crmApi.patchRequest(token, initial.id, form);
      } else {
        await crmApi.createRequest(token, clientId, form);
      }
      toast.success("Запрос сохранён");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Изменить запрос" : "Новый запрос"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Текст запроса *</Label>
            <Textarea
              rows={3}
              value={form.raw_text || ""}
              onChange={(e) => setForm({ ...form, raw_text: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Авто</Label>
              <Input value={form.car_model || ""} onChange={(e) => setForm({ ...form, car_model: e.target.value })} />
            </div>
            <div>
              <Label>Запчасть</Label>
              <Input value={form.part_name || ""} onChange={(e) => setForm({ ...form, part_name: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Точность распознавания (от 0 до 1)</Label>
            <Input
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={form.confidence ?? 0}
              onChange={(e) => setForm({ ...form, confidence: parseFloat(e.target.value) })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Messages tab
// -----------------------------------------------------------------------------

const MEDIA_PLACEHOLDERS = {
  imageMessage: "Изображение",
  videoMessage: "Видео",
  documentMessage: "Документ",
  audioMessage: "Голосовое сообщение",
  voiceMessage: "Голосовое сообщение",
  locationMessage: "Геолокация",
  contactMessage: "Контакт",
  stickerMessage: "Стикер",
};

function MessageMediaPreview({ message, token, isOut }) {
  const isImage = IMAGE_MESSAGE_TYPES.has(message.message_type);
  const isAudio = AUDIO_MESSAGE_TYPES.has(message.message_type);
  const [mediaSrc, setMediaSrc] = useState(message.media_url || null);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const thumbnailSrc = message.media_thumbnail
    ? `data:image/jpeg;base64,${message.media_thumbnail}`
    : null;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (message.media_url) {
      setMediaSrc(message.media_url);
      setMediaError(false);
      return undefined;
    }

    if (!token || (!isImage && !isAudio) || (!message.has_media && !thumbnailSrc)) {
      setMediaSrc(null);
      return undefined;
    }

    setLoadingMedia(true);
    setMediaError(false);
    crmApi
      .getMessageMediaBlob(token, message.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setMediaError(true);
          setMediaSrc(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMedia(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.id, message.media_url, message.has_media, token, isImage, isAudio, thumbnailSrc]);

  if (isImage) {
    const previewSrc = mediaSrc || thumbnailSrc;
    if (loadingMedia && !previewSrc) {
      return <p className="text-xs opacity-70">Загружаем изображение...</p>;
    }
    if (previewSrc) {
      return (
        <a href={mediaSrc || previewSrc} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={previewSrc}
            alt={message.text || "Изображение из WhatsApp"}
            className="mt-1 max-h-72 w-full rounded-md object-contain"
            loading="lazy"
          />
        </a>
      );
    }
    if (mediaError) {
      return <p className="text-xs italic opacity-70">Не удалось загрузить изображение</p>;
    }
    return null;
  }

  if (isAudio) {
    if (loadingMedia && !mediaSrc) {
      return <p className="text-xs opacity-70">Загружаем голосовое...</p>;
    }
    if (mediaSrc) {
      return (
        <audio
          controls
          preload="metadata"
          src={mediaSrc}
          className={`mt-1 w-full max-w-sm ${isOut ? "invert hue-rotate-180" : ""}`}
        />
      );
    }
    if (mediaError) {
      return <p className="text-xs italic opacity-70">Не удалось загрузить голосовое сообщение</p>;
    }
    return null;
  }

  return null;
}

function MessageBubble({ message, token }) {
  const isOut = message.direction === "outgoing";
  const placeholder = MEDIA_PLACEHOLDERS[message.message_type];
  const text = (message.text || "").trim();
  const showMedia = message.has_media || message.media_url || message.media_thumbnail;
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[min(680px,82%)] rounded-lg px-3 py-2 shadow-sm ${
          isOut ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-900"
        }`}
        data-testid={`crm-message-${message.id}`}
      >
        <p className="text-xs opacity-60 mb-1">
          {message.direction === "incoming" ? "Входящее" : "Исходящее"} ·{" "}
          {MESSAGE_TYPE_LABELS[message.message_type] || "Сообщение"} · {fmt(message.timestamp || message.created_at)}
        </p>
        {showMedia && (
          <MessageMediaPreview message={message} token={token} isOut={isOut} />
        )}
        {placeholder && !text && !showMedia ? (
          <p className="italic opacity-80">{placeholder}</p>
        ) : placeholder && !text ? (
          <p className="italic opacity-70 text-xs mt-1">{placeholder}</p>
        ) : placeholder ? (
          <>
            {!showMedia && <p className="italic opacity-70 text-xs mb-1">{placeholder}</p>}
            <p className="whitespace-pre-wrap break-words">{text}</p>
          </>
        ) : (
          <p className="whitespace-pre-wrap break-words">
            {text || <em className="opacity-60">(без текста)</em>}
          </p>
        )}
      </div>
    </div>
  );
}

function MessageComposer({ token, clientId, health, onSent, canEdit }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  if (!canEdit) return null;

  const sendEnabled = !!health?.whatsapp_send_enabled;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const res = await crmApi.sendMessage(token, clientId, trimmed);
      if (!res.success) {
        toast.error(res.error || "Не удалось отправить сообщение");
        return;
      }
      setText("");
      toast.success(`Отправлено (${res.transport || "ok"})`);
      onSent && onSent(res.message);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отправить сообщение"));
    } finally {
      setSending(false);
    }
  };

  if (!sendEnabled) {
    return (
      <div
        data-testid="crm-composer-disabled"
        className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500"
      >
        Отправка сообщений будет доступна после включения отправки WhatsApp.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-end" data-testid="crm-composer">
      <Textarea
        rows={2}
        className="flex-1"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Введите ответ менеджера..."
        disabled={sending}
      />
      <Button type="submit" disabled={sending || !text.trim()} data-testid="crm-composer-submit">
        {sending && <Loader2 className="h-4 w-4 animate-spin" />}
        <Send className="h-4 w-4" />
        Отправить
      </Button>
    </form>
  );
}

function MessagesTab({ token, clientId, health, canEdit }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const items = await crmApi.listMessages(token, clientId, { limit: MESSAGE_PAGE_SIZE });
      setMessages(items);
      setHasMore(items.length === MESSAGE_PAGE_SIZE);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    try {
      const last = messages[messages.length - 1];
      const items = await crmApi.listMessages(token, clientId, {
        limit: MESSAGE_PAGE_SIZE,
        before_id: last.id,
      });
      setMessages([...messages, ...items]);
      setHasMore(items.length === MESSAGE_PAGE_SIZE);
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const reversed = [...messages].reverse();

  return (
    <div className="space-y-3">
      {hasMore && messages.length > 0 && (
        <div className="text-center">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            Загрузить ещё
          </Button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загружаем переписку...
        </div>
      ) : messages.length === 0 ? (
        <EmptyState>Переписки с этим клиентом пока нет.</EmptyState>
      ) : (
        <div className="space-y-3 rounded-lg bg-slate-50 p-3">
          {reversed.map((m) => <MessageBubble key={m.id} message={m} token={token} />)}
        </div>
      )}
      <MessageComposer
        token={token}
        clientId={clientId}
        health={health}
        canEdit={canEdit}
        onSent={() => loadInitial()}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main detail page
// -----------------------------------------------------------------------------

export default function CrmClientDetailPage() {
  const { clientId } = useParams();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [tags, setTags] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stockSubscriptions, setStockSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const initialTab = TABS.some((t) => t.value === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "overview";
  const [tab, setTab] = useState(initialTab);
  const [editOpen, setEditOpen] = useState(false);
  const [tagDialog, setTagDialog] = useState({ open: false, initial: null });
  const [vehicleDialog, setVehicleDialog] = useState({ open: false, initial: null });
  const [dealDialog, setDealDialog] = useState({ open: false, initial: null });
  const [requestDialog, setRequestDialog] = useState({ open: false, initial: null });
  const [health, setHealth] = useState(null);

  const canEdit = userCanEditCrm(user);
  const activeStockWaits = stockSubscriptions.filter((item) => item.status === "active").length;

  useEffect(() => {
    crmApi.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    if (!authLoading && !userHasCrmAccess(user)) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const nextTab = searchParams.get("tab");
    if (nextTab && TABS.some((t) => t.value === nextTab) && nextTab !== tab) {
      setTab(nextTab);
    }
  }, [searchParams, tab]);

  const onTabChange = (value) => {
    setTab(value);
    const next = new URLSearchParams(searchParams);
    if (value && value !== "overview") next.set("tab", value);
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  const reloadAll = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const c = await crmApi.getClient(token, clientId);
      setClient(c);

      const [tagsRes, vehiclesRes, dealsRes, requestsRes, stockRes, ordersRes, paymentsRes] =
        await Promise.allSettled([
          crmApi.listTags(token, clientId),
          crmApi.listVehicles(token, clientId),
          crmApi.listDeals(token, clientId),
          crmApi.listRequests(token, clientId),
          crmApi.listStockSubscriptions(token, clientId),
          crmApi.listCustomerOrdersByClient(token, clientId),
          crmApi.listClientPayments(token, clientId),
        ]);

      const failed = [];
      if (tagsRes.status === "fulfilled") setTags(tagsRes.value);
      else failed.push("теги");
      if (vehiclesRes.status === "fulfilled") setVehicles(vehiclesRes.value);
      else failed.push("автомобили");
      if (dealsRes.status === "fulfilled") setDeals(dealsRes.value);
      else failed.push("сделки");
      if (requestsRes.status === "fulfilled") setRequests(requestsRes.value);
      else failed.push("запросы");
      if (stockRes.status === "fulfilled") setStockSubscriptions(stockRes.value || []);
      else failed.push("ожидание наличия");
      if (ordersRes.status === "fulfilled") {
        const co = ordersRes.value;
        setCustomerOrders(Array.isArray(co) ? co : []);
      } else failed.push("заказы");
      if (paymentsRes.status === "fulfilled") {
        setClientPayments(paymentsRes.value?.items || []);
      } else failed.push("оплаты");

      if (failed.length > 0) {
        toast.warning(`Не удалось загрузить: ${failed.join(", ")}`);
      }
    } catch (err) {
      setClient(null);
      setLoadError(extractCrmError(err, "Не удалось загрузить карточку"));
      toast.error(extractCrmError(err, "Не удалось загрузить карточку"));
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    if (token) reloadAll();
  }, [token, reloadAll]);

  if (loading && !client) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаем карточку клиента...
      </div>
    );
  }

  if (loadError && !client) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Не удалось открыть карточку клиента</CardTitle>
            <CardDescription className="text-red-700">{loadError}</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Button variant="outline" asChild>
              <Link to="/crm/clients">К списку клиентов</Link>
            </Button>
            <Button onClick={reloadAll}>Повторить</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const displayName = client.manual_name || client.customer_name || client.display_name || "Без имени";
  const handleCreateOrder = () => {
    const params = new URLSearchParams({
      client_id: String(client.id),
      source: "manual",
    });
    navigate(`/crm/customer-orders/new?${params.toString()}`);
  };
  const runAutomation = async (label, action) => {
    if (!window.confirm(`${label}?`)) return;
    try {
      const result = await action();
      if (result?.success === false || result?.skipped) {
        toast.warning(result.reason || "Действие не выполнено");
      } else {
        toast.success("Готово");
      }
      await reloadAll();
    } catch (err) {
      toast.error(extractCrmError(err, "Действие не выполнено"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-5">
        <Link to="/crm/clients" className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-950">
          <ArrowLeft className="mr-1 h-4 w-4" /> К списку клиентов
        </Link>
      </div>

      <Card className="mb-4 overflow-hidden border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/70">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <ClientStatusBadge status={client.status} />
                <Badge variant="outline" className={client.auto_reply_disabled ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}>
                  Автоответы {client.auto_reply_disabled ? "отключены" : "включены"}
                </Badge>
              </div>
              <CardTitle className="truncate text-2xl font-semibold text-slate-950">{displayName}</CardTitle>
              <CardDescription className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {client.phone || "Телефон не указан"}
                </span>
                <span className="text-slate-300">/</span>
                <span>{client.whatsapp_chat_id || "Чат не указан"}</span>
              </CardDescription>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setEditOpen(true)}>
                <Edit className="h-4 w-4" /> Изменить
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <DetailMetric label="Сообщения" value={client.counters?.messages ?? 0} icon={MessageSquare} />
            <DetailMetric label="Запросы" value={client.counters?.requests ?? 0} icon={ClipboardList} />
            <DetailMetric
              label="Ждут наличия"
              value={client.counters?.stock_waits ?? activeStockWaits}
              icon={BellRing}
            />
            <DetailMetric label="Сделки" value={client.counters?.deals ?? 0} icon={CheckCircle2} />
            <DetailMetric label="Последняя активность" value={fmt(client.last_message_at)} icon={CalendarClock} />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Основной автомобиль</p>
              <p className="mt-2 flex items-center gap-2 font-semibold text-slate-950">
                <Car className="h-4 w-4 text-slate-400" />
                {client.primary_vehicle
                  ? `${client.primary_vehicle.brand || ""} ${client.primary_vehicle.model || ""}`.trim() || "Автомобиль не указан"
                  : client.detected_car_model || "Автомобиль не указан"}
                {client.primary_vehicle?.year ? `, ${client.primary_vehicle.year}` : ""}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Заметки менеджера</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                {client.notes || "Заметок пока нет"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {canEdit && (
        <Card className="mb-4 border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-950">Быстрые действия</CardTitle>
            <CardDescription>Ручной запуск сообщений и сервисных сценариев для этого клиента.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => runAutomation("Отправить анкету вручную", () => crmApi.sendSurvey(token, client.id))}>
              Отправить анкету вручную
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutomation("Сбросить анкету", () => crmApi.resetSurvey(token, client.id))}>
              Сбросить анкету
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutomation("Повторить приглашение сохранить контакт", () => crmApi.resendSaveContact(token, client.id))}>
              Повторить приглашение сохранить контакт
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutomation("Отправить адрес", () => crmApi.sendAddress(token, client.id))}>
              Отправить адрес
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutomation("Отправить приветствие", () => crmApi.sendWelcome(token, client.id))}>
              Отправить приветствие
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutomation("Поставить напоминание после звонка", () => crmApi.putCallAnchor(token, client.id))}>
              Поставить напоминание после звонка
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runAutomation("Отключить автоответы", () => crmApi.patchClient(token, client.id, { auto_reply_disabled: true }))}
            >
              Отключить автоответы
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                const text = window.prompt("Текст ответа клиента для тестового разбора");
                if (!text) return;
                try {
                  const result = await crmApi.debugParseSurvey(token, client.id, { text, dry_run: true });
                  toast.success(`Источник: ${result.source || "не указан"}, точность ${Math.round((result.parsed?.confidence || 0) * 100)}%`);
                } catch (err) {
                  toast.error(extractCrmError(err, "Не удалось распознать"));
                }
              }}
            >
              Тестовый разбор анкеты
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-slate-100 p-1 sm:grid-cols-3 lg:grid-cols-9">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="relative">
              {t.label}
              {t.value === "stock" && activeStockWaits > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
                  {activeStockWaits}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-4">
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Общая информация</CardTitle>
                <CardDescription>Основные сведения из карточки и источника данных.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Имя из источника</p>
                  <p className="mt-1 font-medium text-slate-900">{client.display_name || "Не указано"}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Распознанный автомобиль</p>
                  <p className="mt-1 font-medium text-slate-900">{client.detected_car_model || "Не указан"}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Автоответы</p>
                  <p className="mt-1 font-medium text-slate-900">{client.auto_reply_disabled ? "Отключены" : "Включены"}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Создан</p>
                  <p className="mt-1 font-medium text-slate-900">{fmt(client.created_at)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <History className="h-4 w-4" />
                  История событий
                </CardTitle>
                <CardDescription>
                  Синхронизация с Google Contacts, правки карточки, автоответы и другие действия.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ActivityFeed key={client.updated_at || client.id} token={token} clientId={clientId} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Переписка</CardTitle>
              <CardDescription>Последние сообщения WhatsApp. Показаны {MESSAGE_PAGE_SIZE} последних; более ранние — по кнопке ниже.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <MessagesTab
                token={token}
                clientId={clientId}
                health={health}
                canEdit={canEdit}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Запросы</CardTitle>
                <CardDescription>Что клиент искал: запчасти, автомобиль и точность распознавания.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => setRequestDialog({ open: true, initial: null })}>
                  <Plus className="h-4 w-4" /> Добавить
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {requests.length === 0 && <EmptyState>Запросов пока нет.</EmptyState>}
              <ul className="space-y-3">
                {requests.map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-4">
                    <div className="flex-1">
                      <p className="font-medium text-slate-950">{r.part_name || "Запчасть не указана"}</p>
                      <p className="mt-1 text-sm text-slate-500">Автомобиль: {r.car_model || "не указан"}</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{r.raw_text}</p>
                      <p className="mt-2 text-xs text-slate-400">Точность распознавания: {formatConfidence(r.confidence)}</p>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setRequestDialog({ open: true, initial: r })}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Удалить запрос?")) return;
                            try {
                              await crmApi.deleteRequest(token, r.id);
                              toast.success("Удалено");
                              reloadAll();
                            } catch (err) {
                              toast.error(extractCrmError(err));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stock">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BellRing className="h-4 w-4 text-amber-600" />
                Ждут поступления на склад
              </CardTitle>
              <CardDescription>
                Это не вкладка «Запросы». Здесь товары, по которым клиент ждёт WhatsApp при появлении на складе.
                Создать: прайс-лист → товар → «Клиенту». Общий список всех клиентов:{" "}
                <Link to="/crm/stock-watches" className="font-medium text-amber-700 hover:underline">
                  Ожидание наличия
                </Link>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stockSubscriptions.length === 0 && (
                <EmptyState>Подписок на наличие пока нет.</EmptyState>
              )}
              <ul className="space-y-3">
                {stockSubscriptions.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-950">{item.product_name}</p>
                        <Badge
                          variant="outline"
                          className={STOCK_STATUS_STYLES[item.status] || STOCK_STATUS_STYLES.cancelled}
                        >
                          {STOCK_STATUS_LABELS[item.status] || item.status}
                        </Badge>
                        {item.product_available === true ? (
                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                            Сейчас в наличии
                            {typeof item.product_in_stock === "number" ? ` · ${item.product_in_stock}` : ""}
                          </Badge>
                        ) : item.product_available === false ? (
                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                            Нет на складе
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {[item.product_code && `код ${item.product_code}`, item.product_brand, item.product_articul]
                          .filter(Boolean)
                          .join(" · ") || "Без кода"}
                      </p>
                      {item.notes && (
                        <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{item.notes}</p>
                      )}
                      <p className="mt-2 text-xs text-slate-400">
                        Запросил: {fmt(item.created_at)}
                        {item.notified_at ? ` · уведомлён ${fmt(item.notified_at)}` : ""}
                        {item.last_error ? ` · ошибка: ${item.last_error}` : ""}
                      </p>
                    </div>
                    {canEdit && item.status === "active" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!window.confirm("Отменить ожидание наличия?")) return;
                          try {
                            await crmApi.cancelStockSubscription(token, item.id);
                            toast.success("Подписка отменена");
                            reloadAll();
                          } catch (err) {
                            toast.error(extractCrmError(err));
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Сделки</CardTitle>
                <CardDescription>Коммерческие возможности и текущий этап работы.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => setDealDialog({ open: true, initial: null })}>
                  <Plus className="h-4 w-4" /> Добавить
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {deals.length === 0 && <EmptyState>Сделок пока нет.</EmptyState>}
              <ul className="space-y-3">
                {deals.map((d) => (
                  <li key={d.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-4">
                    <div className="flex-1">
                      <p className="font-medium text-slate-950">{d.title}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                          {getDealStageLabel(d.stage)}
                        </Badge>
                        <span>Обновлено: {fmt(d.updated_at)}</span>
                      </div>
                      {d.notes && <p className="mt-2 text-sm text-slate-700">{d.notes}</p>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setDealDialog({ open: true, initial: d })}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Удалить сделку?")) return;
                            try {
                              await crmApi.deleteDeal(token, d.id);
                              toast.success("Удалено");
                              reloadAll();
                            } catch (err) {
                              toast.error(extractCrmError(err));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Теги</CardTitle>
                <CardDescription>Короткие пометки для сегментации и приоритизации клиентов.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => setTagDialog({ open: true, initial: null })}>
                  <Plus className="h-4 w-4" /> Добавить
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {tags.length === 0 && <EmptyState>Тегов пока нет.</EmptyState>}
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span
                    key={t.id}
                    className={`inline-flex items-center gap-1 border rounded-full px-2 py-1 text-xs ${TAG_COLOR_HEX[t.color] || ""}`}
                  >
                    <TagIcon className="w-3 h-3" />
                    {t.tag}
                    {canEdit && (
                      <>
                        <button
                          className="ml-1 rounded px-1 text-[10px] underline"
                          onClick={() => setTagDialog({ open: true, initial: t })}
                        >
                          изменить
                        </button>
                        <button
                          className="ml-1 rounded px-1 text-[10px] underline"
                          onClick={async () => {
                            if (!window.confirm("Удалить тег?")) return;
                            try {
                              await crmApi.deleteTag(token, t.id);
                              toast.success("Удалено");
                              reloadAll();
                            } catch (err) {
                              toast.error(extractCrmError(err));
                            }
                          }}
                        >
                          удалить
                        </button>
                      </>
                    )}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vehicles">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Автомобили</CardTitle>
                <CardDescription>Автомобили клиента, VIN и источник информации.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={() => setVehicleDialog({ open: true, initial: null })}>
                  <Plus className="h-4 w-4" /> Добавить
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 && <EmptyState>Автомобилей пока нет.</EmptyState>}
              <ul className="space-y-3">
                {vehicles.map((v) => (
                  <li key={v.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-4">
                    <div className="flex-1">
                      <p className="flex items-center gap-2 font-medium text-slate-950">
                        {v.is_primary && <Star className="h-4 w-4 text-amber-500" />}
                        {`${v.brand || ""} ${v.model || ""}`.trim() || "—"}
                        {v.year ? ` (${v.year})` : ""}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        {v.vin ? `VIN: ${v.vin} · ` : ""}
                        Источник: {VEHICLE_SOURCE_LABELS[v.source] || "не указан"}
                        {v.confidence !== null && v.confidence !== undefined ? ` · Точность: ${formatConfidence(v.confidence)}` : ""}
                      </p>
                      {v.comment && <p className="mt-2 text-sm text-slate-700">{v.comment}</p>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        {!v.is_primary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Сделать основным"
                            onClick={async () => {
                              try {
                                await crmApi.makePrimary(token, v.id);
                                toast.success("Основной автомобиль обновлён");
                                reloadAll();
                              } catch (err) {
                                toast.error(extractCrmError(err));
                              }
                            }}
                          >
                            <Star className="h-4 w-4 text-slate-400" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setVehicleDialog({ open: true, initial: v })}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Удалить автомобиль?")) return;
                            try {
                              await crmApi.deleteVehicle(token, v.id);
                              toast.success("Удалено");
                              reloadAll();
                            } catch (err) {
                              toast.error(extractCrmError(err));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Заказы покупателей</CardTitle>
                <CardDescription>История заказов клиента и быстрый переход к редактированию.</CardDescription>
              </div>
              {canEdit && (
                <Button size="sm" onClick={handleCreateOrder}>
                  <Plus className="h-4 w-4" /> Создать заказ
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {customerOrders.length === 0 && <EmptyState>Заказов покупателей пока нет.</EmptyState>}
              <ul className="space-y-3">
                {customerOrders.map((order) => (
                  <li
                    key={order.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-950">{order.order_number}</p>
                        <Badge
                          variant="outline"
                          className={CUSTOMER_ORDER_STATUS_STYLES[order.status] || "border-slate-200 bg-slate-50 text-slate-700"}
                        >
                          {getCustomerOrderStatusLabel(order.status)}
                        </Badge>
                        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                          {getCustomerOrderSourceLabel(order.source)}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Package className="h-3.5 w-3.5" />
                          {order.item_count ?? 0} поз.
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <ClipboardList className="h-3.5 w-3.5" />
                          {formatOrderMoney(order.total_amount, order.currency)}
                        </span>
                        <span>Создан: {fmt(order.created_at)}</span>
                        {order.promised_delivery_date && (
                          <span>Поставка: {fmtDate(order.promised_delivery_date)}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/crm/customer-orders/${order.id}/edit`)}
                    >
                      Открыть
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="h-4 w-4" />
                Оплаты клиента
              </CardTitle>
              <CardDescription>
                Все оплаты по заказам клиента. Добавление оплат — в карточке конкретного заказа.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clientPayments.length === 0 && (
                <EmptyState>Оплат по заказам клиента пока нет.</EmptyState>
              )}
              {clientPayments.length > 0 && (
                <ul className="space-y-3">
                  {clientPayments.map((payment) => (
                    <li
                      key={payment.id}
                      className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-slate-950">{payment.order_number}</p>
                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                            {getCustomerPaymentMethodLabel(payment.payment_method)}
                          </Badge>
                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                            {getCustomerPaymentStatusLabel(payment.status)}
                          </Badge>
                        </div>
                        <p className="mt-2 text-sm font-semibold text-slate-950">
                          {formatOrderMoney(payment.amount, "KZT")}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {fmt(payment.paid_at || payment.created_at)}
                          {payment.comment ? ` · ${payment.comment}` : ""}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/crm/customer-orders/${payment.order_id}/edit`)}
                      >
                        Открыть заказ
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ClientEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        client={client}
        token={token}
        onSaved={(updated) => setClient({ ...client, ...updated })}
      />
      <TagDialog
        open={tagDialog.open}
        onOpenChange={(v) => setTagDialog({ ...tagDialog, open: v })}
        clientId={clientId}
        initial={tagDialog.initial}
        token={token}
        onSaved={reloadAll}
      />
      <VehicleDialog
        open={vehicleDialog.open}
        onOpenChange={(v) => setVehicleDialog({ ...vehicleDialog, open: v })}
        clientId={clientId}
        initial={vehicleDialog.initial}
        token={token}
        onSaved={reloadAll}
      />
      <DealDialog
        open={dealDialog.open}
        onOpenChange={(v) => setDealDialog({ ...dealDialog, open: v })}
        clientId={clientId}
        initial={dealDialog.initial}
        token={token}
        onSaved={reloadAll}
      />
      <RequestDialog
        open={requestDialog.open}
        onOpenChange={(v) => setRequestDialog({ ...requestDialog, open: v })}
        clientId={clientId}
        initial={requestDialog.initial}
        token={token}
        onSaved={reloadAll}
      />
    </div>
  );
}
